import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { Navigate } from "@tanstack/react-router";
import React, { useState } from "react";
import { userProfile } from "app/router";
import { ReactComponent as BioIcon } from "../../assets/img/user-profile/bio.svg";
import { ReactComponent as EmailIcon } from "../../assets/img/user-profile/email.svg";
import { ReactComponent as OrganizationIcon } from "../../assets/img/user-profile/organization.svg";
import { ReactComponent as PhoneIcon } from "../../assets/img/user-profile/phone.svg";
import { ReactComponent as ProfileIcon } from "../../assets/img/user-profile/profile.svg";
import { Button, Spinner } from "components/elements";
import { Avatar } from "components/elements";
import { ProseDocument } from "components/elements";
import { useCreateConversation } from "hooks/useCreateConversation";
import { useCurrentUser } from "hooks/useCurrentUser";
import { usePublicUser } from "hooks/usePublicUser";
import { useUserTimezone } from "hooks/useUserTimezone";
import { formatISODate } from "lib/formatISODate";
import { renderSanitizedHTML } from "lib/renderSanitizedHTML";
import { trpc } from "lib/trpc";
import { ClientPost, ClientUser, FriendRequestStatus } from "@compass/shared/types";

export const UserProfile = () => {
    const timeZone = useUserTimezone();
    const [activeTab, setActiveTab] = useState<"posts" | "blogs">("posts");
    const id = parseInt(useParams({ from: userProfile.id }).id);
    const currentUser = useCurrentUser();
    const queryClient = useQueryClient();
    const _isCurrentUser = useCurrentUser()?.id === id;
    const { user, isInitialLoading } = usePublicUser(id);
    const createConversation = useCreateConversation(user?.id?.toString() || "");
    const trpcUtils = trpc.useContext();
    const isFriend = currentUser?.friends.some((item) => item.id === id);
    const isSentFriendRequest = currentUser?.sentFriendRequest.some(
        (item) => item.receiverId === id,
    );
    const isRecivedFriendRequest = currentUser?.receivedFriendRequests.some(
        (item) => item.senderId === id,
    );
    const recivedFriendRequest = currentUser?.receivedFriendRequests.find(
        (item) => item.senderId === id,
    );
    const friendRequestSentMutation = trpc.users.friendRequestSent.useMutation();
    const friendRequestDeleteMutation = trpc.users.friendRequestDelete.useMutation();
    const friendRequestMutation = trpc.users.friendRequestResponse.useMutation();

    const navigate = useNavigate();

    const handleFriendRequestResponse = ({
        id,
        status,
    }: {
        id: number;
        status: FriendRequestStatus;
    }) => {
        try {
            friendRequestMutation.mutate(
                { id: id, status: status },
                {
                    onSuccess: () => {
                        trpcUtils.users.me.invalidate();
                        trpcUtils.users.getAllUser.invalidate();
                    },
                },
            );
        } catch (err) {
            console.error(err);
        }
    };

    const handleSentRequest = ({ id, status }: { id: number; status: FriendRequestStatus }) => {
        const findData = currentUser?.receivedFriendRequests.find((item) => item.senderId === id);
        if (findData) {
            handleFriendRequestResponse({ id: findData.id, status });
        }
    };

    const handleFriendRequestDelete = ({ id }: { id: number }) => {
        try {
            friendRequestDeleteMutation.mutate(
                { userId: id },
                {
                    onSuccess: () => {
                        trpcUtils.users.me.invalidate();
                        trpcUtils.users.getAllUser.invalidate();
                    },
                },
            );
        } catch (err) {
            console.error(err);
        }
    };

    const manageButton = ({ mode, id }: { mode: string; id: number }) => {
        switch (mode) {
            case "sent":
                return (
                    <Button
                        size="smaller"
                        onClick={() => handleFriendRequestDelete({ id })}
                        className="bg-gray-500 px-4 py-1.5 text-sm text-white hover:bg-gray-600"
                    >
                        Cancel
                    </Button>
                );
            case "request":
                return (
                    <div className="flex gap-2">
                        <Button
                            size="smaller"
                            onClick={() =>
                                recivedFriendRequest?.id &&
                                handleFriendRequestResponse({
                                    id: recivedFriendRequest.id,
                                    status: "ACCEPTED",
                                })
                            }
                            className="bg-green-500 px-4 py-1.5 text-sm text-white hover:bg-green-600"
                        >
                            Accept
                        </Button>
                        <Button
                            size="smaller"
                            onClick={() =>
                                recivedFriendRequest?.id &&
                                handleSentRequest({
                                    id: recivedFriendRequest.id,
                                    status: "DECLINED",
                                })
                            }
                            className="bg-red-500 px-4 py-1.5 text-sm text-white hover:bg-red-600"
                        >
                            Decline
                        </Button>
                    </div>
                );
            default:
                return (
                    <Button
                        size="smaller"
                        onClick={() => handleFriendRequestSentResponse({ id })}
                        className="bg-blue-500 px-4 py-1.5 text-sm text-white hover:bg-blue-600"
                    >
                        Add Friend
                    </Button>
                );
        }
    };

    const handleFriendRequestSentResponse = ({ id }: { id: number }) => {
        const previousData = queryClient.getQueryData(["users.getAllUser"]);

        queryClient.setQueryData(["users.getAllUser"], (old: any) =>
            old
                ? old.map((user: ClientUser) =>
                      user.id === id ? { ...user, sentFriendRequest: true } : user,
                  )
                : old,
        );

        friendRequestSentMutation.mutate(
            { userId: id },
            {
                onError: () => {
                    queryClient.setQueryData(["users.getAllUser"], previousData);
                },
                onSuccess: () => {
                    trpcUtils.users.me.invalidate();
                    trpcUtils.users.getAllUser.invalidate();
                },
            },
        );
    };

    if (isInitialLoading) {
        return (
            <span className="absolute inset-0 flex items-center justify-center">
                <Spinner className="h-10 w-10 text-slate-500" />
            </span>
        );
    }

    if (_isCurrentUser) {
        return <Navigate to="/profile/edit/$id" params={{ id: `${id}` }} />;
    }

    const handleConversation = async () => {
        if (!user) return;

        const conversationId = await createConversation();

        if (conversationId) {
            navigate({ to: "/messages/$id", params: { id: conversationId } });
        }
    };

    if (!user) {
        return (
            <div className="flex h-screen items-center justify-center text-center font-sans text-[28px] font-semibold text-[#424242]">
                Sorry, this page does not exist.
            </div>
        );
    }

    const isDeactivated = !!user.deletedAt;
    const isInteractable = !isDeactivated && user.isSocialEnabled;

    return (
        <>
            {user && (
                <div className="pt-6">
                    {isDeactivated && (
                        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                            This user has been deactivated.
                        </div>
                    )}
                    <div className="font-sans text-[28px] font-semibold text-[#424242]">
                        Profile
                    </div>
                    <div className="mt-5 grid grid-cols-3 gap-6">
                        {/* Profile Picture */}
                        <div className="flex flex-col items-center rounded-2xl border border-gray-100 bg-[#2da8e0] p-8 shadow-none">
                            <div className="mt-5">
                                <Avatar
                                    photoUrl={user?.avatarUrl}
                                    name={user.name}
                                    className="h-24 w-24 rounded-full"
                                />
                            </div>
                            <div className="mt-4 text-xl font-medium text-white">{user.name}</div>
                            <div className="mt-2  text-white">{user.email}</div>
                            {isInteractable && (
                                <>
                                    <div className="mt-8">
                                        <Button
                                            variant="primary"
                                            size="normal"
                                            className="bg-white text-[#2da8e0] hover:bg-gray-100"
                                            onClick={handleConversation}
                                        >
                                            Message
                                        </Button>
                                    </div>
                                    {!isFriend && currentUser && (
                                        <div className="mt-8">
                                            {manageButton({
                                                mode: isRecivedFriendRequest
                                                    ? "request"
                                                    : isSentFriendRequest
                                                      ? "sent"
                                                      : "new",
                                                id: id,
                                            })}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Profile Details */}
                        <div className="col-span-2 ">
                            <div className="bg-brand-polar grid grid-cols-2 items-center gap-8 rounded-2xl border border-[#c8c8c8] p-8 shadow-none">
                                {/* Full Name */}
                                <div className="flex items-center space-x-3">
                                    <div className="flex items-center justify-center rounded-lg bg-[#cee5f0] p-2">
                                        <ProfileIcon className="h-5 w-5 " />
                                    </div>
                                    <div>
                                        <div className=" text-gray-800">{user.name}</div>
                                    </div>
                                </div>

                                {/* Phone Number */}
                                {/* <div className="flex items-center space-x-3">
                                    <div className="flex items-center justify-center rounded-lg bg-[#cee5f0] p-2">
                                        <PhoneIcon className="h-5 w-5 " />
                                    </div>
                                    <div>
                                        <div className="  text-gray-700">-</div>
                                    </div>
                                </div> */}

                                {/* Organization */}
                                <div className="flex items-center space-x-3">
                                    <div className="flex items-center justify-center rounded-lg bg-[#cee5f0] p-2">
                                        <OrganizationIcon className="h-5 w-5 " />
                                    </div>
                                    <div>
                                        <div className=" text-gray-700">
                                            {user.meta.organization || "-"}
                                        </div>
                                    </div>
                                </div>

                                {/*Email */}
                                {/* <div className="flex items-center space-x-3">
                                    <div className="flex items-center justify-center rounded-lg bg-[#cee5f0] p-2">
                                        <EmailIcon className="h-5 w-5 " />
                                    </div>
                                    <div>
                                        <div className="  text-gray-700">-</div>
                                    </div>
                                </div> */}

                                {/* Bio */}
                                <div className="col-span-2 flex items-center space-x-3">
                                    <div className="flex items-center justify-center rounded-lg bg-[#cee5f0] p-2">
                                        <BioIcon className="h-5 w-5 " />
                                    </div>
                                    <div>
                                        <div className=" text-gray-700">{user.meta.bio || "-"}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Ask me about */}
                            <div className="bg-brand-polar mt-4 grid w-full rounded-2xl border border-[#c8c8c8] p-10 shadow-none">
                                <div className="">
                                    <div className="text-lg font-semibold text-[#424242]">
                                        Ask me about...
                                    </div>

                                    {/* Tags */}
                                    <div className="mt-4 text-sm text-gray-700">
                                        {user.aboutMeContentJson ? (
                                            <ProseDocument doc={user.aboutMeContentJson} />
                                        ) : (
                                            <span className="text-gray-400"></span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Activities */}
                        <div className="bg-brand-polar col-start-1 col-end-4 rounded-2xl border border-[#c8c8c8] px-8 py-6 shadow-none">
                            <div className="text-lg font-semibold text-[#424242]">Activities</div>
                            {/*<div className="mt-4 flex items-center">
                                <Button
                                    variant={activeTab === "posts" ? "primary" : "secondary"}
                                    size="smaller"
                                    onClick={() => setActiveTab("posts")}
                                    className={`mr-2 focus:outline-none focus:ring-0 ${
                                        activeTab === "posts"
                                            ? "text-white"
                                            : "bg-[#f7f7f7] text-gray-700 hover:bg-gray-100"
                                    }`}
                                >
                                    Posts
                                </Button>
                            </div>*/}
                            {activeTab === "posts" && user.posts ? (
                                <div className="mt-4 max-h-96 overflow-y-auto">
                                    {/* Posts content */}
                                    {(user.posts as ClientPost[]).length > 0 ? (
                                        (user.posts as ClientPost[]).map((post) => (
                                            <div key={post.id} className="">
                                                <Link
                                                    to="/social/post/$id"
                                                    className="flex flex-row items-center gap-4 py-4"
                                                    params={{
                                                        id: post.id.toString(),
                                                    }}
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <Avatar
                                                            photoUrl={
                                                                post.author.type === "identified" ||
                                                                post.author.type ===
                                                                    "anonymous-self"
                                                                    ? post.author.avatarUrl
                                                                    : null
                                                            }
                                                            name={post.author.name}
                                                            className="h-12 w-12 rounded"
                                                        />
                                                        <div>
                                                            <div className="text-sm font-semibold text-gray-700">
                                                                {post.author.name}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                {formatISODate(
                                                                    post.createdAt,
                                                                    timeZone,
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="text-sm text-gray-700"
                                                        {...renderSanitizedHTML(post.title)}
                                                    />
                                                </Link>
                                            </div>
                                        ))
                                    ) : (
                                        <span>No posts available</span>
                                    )}
                                </div>
                            ) : (
                                <div className="mt-6 max-h-96 overflow-y-auto">
                                    {/* Blogs content */}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
